import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from 'store/app/entities/baseQuery';

// Extract the actual value/ID from a filter item (handles both primitives and objects)
const extractValue = item => {
  if (item === null || item === undefined) return null;
  if (typeof item === 'object') return item.value || item._id || item.id || null;
  return item;
};

// Extract values from a filter array
const extractValues = arr => (arr || []).map(extractValue).filter(Boolean);

// Check if filter is in object format { values: [...], operator: 'is' }
const isObjectFormat = filter =>
  filter && typeof filter === 'object' && !Array.isArray(filter) && 'values' in filter;

// Helper to check if a filter has values (handles both array and object format)
const hasFilterValues = filter => {
  if (!filter) return false;
  // Object format: { values: [...], operator: 'is' }
  if (isObjectFormat(filter)) {
    // isSet/isNotSet don't need values
    if (filter.operator === 'isSet' || filter.operator === 'isNotSet') return true;
    return filter.values && filter.values.length > 0;
  }
  // Array format (backward compatible)
  if (Array.isArray(filter)) return filter.length > 0;
  return false;
};

// Get values from a filter (handles both array and object format)
const getFilterValues = filter => {
  if (!filter) return [];
  if (isObjectFormat(filter)) return extractValues(filter.values);
  if (Array.isArray(filter)) return extractValues(filter);
  return [];
};

// Get operator from a filter
const getFilterOperator = filter => {
  if (filter?.operator) return filter.operator;
  return 'is';
};

// Build filters array for POST body
const buildFiltersArray = ({
  team,
  department,
  location,
  jobTitle,
  division,
  reportsTo,
  tag,
} = {}) => {
  const filters = [];
  const filterDefs = [
    { field: 'team', value: team },
    { field: 'department', value: department },
    { field: 'location', value: location },
    { field: 'jobTitle', value: jobTitle },
    { field: 'division', value: division },
    { field: 'reportsTo', value: reportsTo },
    { field: 'tag', value: tag },
  ];

  filterDefs.forEach(({ field, value }) => {
    if (hasFilterValues(value)) {
      filters.push({
        field,
        operator: getFilterOperator(value),
        values: getFilterValues(value),
      });
    }
  });

  return filters;
};

// Build POST body for analytics endpoints
const buildAnalyticsBody = ({ startDate, endDate, ...filterProps } = {}) => ({
  startDate,
  endDate,
  filters: buildFiltersArray(filterProps),
});

// Helper to generate cache tag ID from all filters
const buildTagId = (prefix, filters = {}) => {
  const { startDate, endDate } = filters;
  // Use the built filters array for consistent cache key
  const filtersArray = buildFiltersArray(filters);
  return `${prefix}-${startDate}-${endDate}-${JSON.stringify(filtersArray)}`;
};

// Build query string for GET analytics endpoints (simple / non-advanced view)
const buildSimpleQueryString = (params = {}) => {
  const { startDate, endDate } = params;
  const search = new URLSearchParams();
  if (startDate) search.set('startDate', startDate);
  if (endDate) search.set('endDate', endDate);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

// Build POST body for activations-per-team (filters only; backend uses filter teams as top teams)
const buildActivationsPerTeamBody = (filters = {}) => ({
  filters: buildFiltersArray(filters),
});

// Build POST body for team-health-index (date range + team only; sortBy, order, limit, skip for rankings)
const buildTeamHealthIndexBody = ({
  startDate,
  endDate,
  team,
  sortBy = 'health_index',
  order = 'desc',
  limit = 10,
  skip = 0,
} = {}) => ({
  startDate,
  endDate,
  filters: buildFiltersArray({ team }),
  sortBy,
  order,
  limit,
  skip,
});

// Cache tag for team health index including sort/limit/skip
const buildTeamHealthIndexTagId = (filters = {}) => {
  const { startDate, endDate, sortBy, order, limit, skip } = filters;
  const filtersArray = buildFiltersArray(filters);
  return `teamHealthIndex-${startDate}-${endDate}-${JSON.stringify(
    filtersArray,
  )}-${sortBy}-${order}-${limit}-${skip ?? 0}`;
};

const analyticsEndpoints = build => ({
  getActivations: build.query({
    query: ({ team = '' } = {}) => `v1/analytics/activations${team ? `?team=${team}` : ''}`,
    transformResponse: response => response.data,
    providesTags: [{ type: 'AnalyticsChart', id: 'activations' }],
  }),
  getActiveUsers: build.query({
    query: filters => ({
      url: 'v1/analytics/active-users',
      method: 'POST',
      body: buildAnalyticsBody(filters),
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, filters = {}) => [
      { type: 'AnalyticsChart', id: buildTagId('activeUsers', filters) },
    ],
  }),
  getMessageCounts: build.query({
    query: filters => ({
      url: 'v1/analytics/message-counts',
      method: 'POST',
      body: buildAnalyticsBody(filters),
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, filters = {}) => [
      { type: 'AnalyticsChart', id: buildTagId('messageCounts', filters) },
    ],
  }),
  getSessionsDaily: build.query({
    query: filters => ({
      url: 'v1/analytics/sessions-daily',
      method: 'POST',
      body: buildAnalyticsBody(filters),
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, filters = {}) => [
      { type: 'AnalyticsChart', id: buildTagId('sessionsDaily', filters) },
    ],
  }),
  getUsersByPlatform: build.query({
    query: filters => ({
      url: 'v1/analytics/users-by-platform',
      method: 'POST',
      body: buildAnalyticsBody(filters),
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, filters = {}) => [
      { type: 'AnalyticsChart', id: buildTagId('usersByPlatform', filters) },
    ],
  }),
  getSessionsPerTeam: build.query({
    query: ({ startDate, endDate, teamId }) =>
      `v1/analytics/sessionsPerTeam?startDate=${startDate}&endDate=${endDate}&teamId=${teamId}`,
    transformResponse: response => response.data,
    providesTags: (result, error, { teamId }) => [
      { type: 'AnalyticsChart', id: `sessionsPerTeam-${teamId}` },
    ],
  }),
  getSessionsByPlatform: build.query({
    query: ({ startDate, endDate, teamId }) =>
      `v1/analytics/sessionsByPlatform?startDate=${startDate}&endDate=${endDate}&teamId=${teamId}`,
    transformResponse: response => response.data,
    providesTags: (result, error, { teamId }) => [
      { type: 'AnalyticsChart', id: `sessionsByPlatform-${teamId}` },
    ],
  }),
  getActiveUsersPerTeam: build.query({
    query: ({ startDate, endDate, teamId }) =>
      `v1/analytics/activeUsersPerTeam?startDate=${startDate}&endDate=${endDate}&teamId=${teamId}`,
    transformResponse: response => response.data,
    providesTags: (result, error, { teamId }) => [
      { type: 'AnalyticsChart', id: `activeUsersPerTeam-${teamId}` },
    ],
  }),
  getMessageCountsPerTeam: build.query({
    query: ({ startDate, endDate, teamId }) =>
      `v1/analytics/messageCountsPerTeam?startDate=${startDate}&endDate=${endDate}&teamId=${teamId}`,
    transformResponse: response => response.data,
    providesTags: (result, error, { teamId }) => [
      { type: 'AnalyticsChart', id: `messageCountsPerTeam-${teamId}` },
    ],
  }),
  getCompareTeams: build.query({
    query: ({ startDate, endDate, teams }) =>
      `v1/analytics/compareTeams?startDate=${startDate}&endDate=${endDate}&teams=${teams}`,
    transformResponse: response => response.data,
    providesTags: [{ type: 'AnalyticsChart', id: 'compareTeams' }],
  }),
  getTeamHealthIndex: build.query({
    query: ({ startDate, endDate, team, sortBy, order, limit, skip } = {}) => ({
      url: 'v1/analytics/team-health-index',
      method: 'POST',
      body: buildTeamHealthIndexBody({
        startDate,
        endDate,
        team,
        sortBy,
        order,
        limit,
        skip,
      }),
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, args = {}) => [
      { type: 'AnalyticsChart', id: buildTeamHealthIndexTagId(args) },
    ],
  }),
  // Get distinct values for filter fields (department, location, jobTitle, etc.)
  getFilterOptions: build.query({
    query: ({ field }) => `v1/analytics/filter-options/${field}`,
    transformResponse: response => response.data,
    providesTags: (result, error, { field }) => [
      { type: 'AnalyticsChart', id: `filterOptions-${field}` },
    ],
  }),

  // GET endpoints (used when company does not have advanced-analytics)
  getActiveUsersSimple: build.query({
    query: (params = {}) => `v1/analytics/active-users${buildSimpleQueryString(params)}`,
    transformResponse: response => response.data,
    providesTags: [{ type: 'AnalyticsChart', id: 'activeUsersSimple' }],
  }),
  getMessageCountsSimple: build.query({
    query: (params = {}) => `v1/analytics/message-counts${buildSimpleQueryString(params)}`,
    transformResponse: response => response.data,
    providesTags: [{ type: 'AnalyticsChart', id: 'messageCountsSimple' }],
  }),
  getSessionsDailySimple: build.query({
    query: (params = {}) => `v1/analytics/sessions-daily${buildSimpleQueryString(params)}`,
    transformResponse: response => response.data,
    providesTags: [{ type: 'AnalyticsChart', id: 'sessionsDailySimple' }],
  }),
  getUsersByPlatformSimple: build.query({
    query: (params = {}) => `v1/analytics/users-by-platform${buildSimpleQueryString(params)}`,
    transformResponse: response => response.data,
    providesTags: [{ type: 'AnalyticsChart', id: 'usersByPlatformSimple' }],
  }),

  getActivationsPerTeamPost: build.query({
    query: filters => ({
      url: 'v1/analytics/activations-per-team',
      method: 'POST',
      body: buildActivationsPerTeamBody(filters),
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, filters = {}) => [
      {
        type: 'AnalyticsChart',
        id: `activationsPerTeam-${JSON.stringify(buildFiltersArray(filters))}`,
      },
    ],
  }),

  getInitiativeDashboard: build.query({
    query: ({ startDate, endDate } = {}) => {
      const qs = buildSimpleQueryString({ startDate, endDate });
      return `v1/analytics/initiatives/dashboard${qs}`;
    },
    transformResponse: response => response.data,
    providesTags: (result, error, { startDate, endDate } = {}) => [
      { type: 'AnalyticsChart', id: `initiativeDashboard-${startDate}-${endDate}` },
    ],
  }),

  getInitiativeSummary: build.query({
    query: ({ startDate, endDate, sortBy, order, limit, offset } = {}) => {
      const search = new URLSearchParams();
      if (startDate) search.set('startDate', startDate);
      if (endDate) search.set('endDate', endDate);
      if (sortBy) search.set('sortBy', sortBy);
      if (order) search.set('order', order);
      if (limit != null) search.set('limit', String(limit));
      if (offset != null) search.set('offset', String(offset));
      const qs = search.toString();
      return `v1/analytics/initiatives/summary${qs ? `?${qs}` : ''}`;
    },
    transformResponse: response => response.data,
    providesTags: (result, error, args = {}) => [
      {
        type: 'AnalyticsChart',
        id: `initiativeSummary-${args.startDate}-${args.endDate}-${args.sortBy}-${args.order}-${args.limit}-${args.offset}`,
      },
    ],
  }),

  getInitiativeConversions: build.query({
    query: ({ startDate, endDate } = {}) => {
      const qs = buildSimpleQueryString({ startDate, endDate });
      return `v1/analytics/initiatives/conversions${qs}`;
    },
    transformResponse: response => response.data,
    providesTags: (result, error, { startDate, endDate } = {}) => [
      { type: 'AnalyticsChart', id: `initiativeConversions-${startDate}-${endDate}` },
    ],
  }),

  getInitiativeStats: build.query({
    query: ({ id, startDate, endDate } = {}) => {
      const qs = buildSimpleQueryString({ startDate, endDate });
      return `v1/analytics/initiatives/${id}${qs}`;
    },
    transformResponse: response => transformInitiativeStatsResponse(response.data),
    providesTags: (result, error, { id, startDate, endDate } = {}) => [
      { type: 'AnalyticsChart', id: `initiativeStats-${id}-${startDate}-${endDate}` },
    ],
  }),

  exportInitiativeStats: build.query({
    query: ({ id, startDate, endDate } = {}) => {
      const qs = buildSimpleQueryString({ startDate, endDate });
      return {
        url: `v1/analytics/initiatives/${id}/export${qs}`,
        cache: 'no-store',
        responseHandler: async response => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get('Content-Disposition');
          return { blob, contentDisposition };
        },
      };
    },
  }),

  getInitiativeCompare: build.query({
    query: ({ ids, startDate, endDate } = {}) => {
      const search = new URLSearchParams();
      if (ids?.length) search.set('ids', ids.join(','));
      if (startDate) search.set('startDate', startDate);
      if (endDate) search.set('endDate', endDate);
      const qs = search.toString();
      return `v1/analytics/initiatives/compare${qs ? `?${qs}` : ''}`;
    },
    transformResponse: response => response.data,
    providesTags: (result, error, { ids, startDate, endDate } = {}) => [
      {
        type: 'AnalyticsChart',
        id: `initiativeCompare-${(ids || []).join('-')}-${startDate}-${endDate}`,
      },
    ],
  }),

  getEngagementLeaderboards: build.query({
    query: ({ sortBy, order, limit, skip, startDate, endDate, teamId, channelId } = {}) => {
      const search = new URLSearchParams();
      if (sortBy) search.set('sortBy', sortBy);
      if (order) search.set('order', order);
      if (limit != null) search.set('limit', String(limit));
      if (skip != null) search.set('skip', String(skip));
      if (startDate) search.set('startDate', startDate);
      if (endDate) search.set('endDate', endDate);
      if (teamId) search.set('team', teamId);
      if (channelId) search.set('channel', channelId);
      const qs = search.toString();
      return `v1/analytics/leaderboards/engagement${qs ? `?${qs}` : ''}`;
    },
    transformResponse: response => response.data,
    providesTags: (result, error, args = {}) => [
      {
        type: 'AnalyticsChart',
        id: `engagementLeaderboards-${args.sortBy}-${args.order}-${args.startDate}-${args.endDate}-${args.teamId}-${args.channelId}-${args.skip}`,
      },
    ],
  }),
});

export function transformInitiativeStatsResponse(d) {
  const m = d?.metrics || {};
  const trends = d?.trends || {};
  return {
    ...d?.initiative,
    kpis: {
      goalCompletionRate: m.goalPercent ?? 0,
      reach: m.uniqueRecipients ?? 0,
      responses: m.totalReads ?? 0,
      goalCompletionRateChange: trends.goalCompletionRateChange,
      reachChange: trends.reachChange,
      responsesChange: trends.responsesChange,
    },
    metrics: m,
    trends,
    dailyTrend: (d?.dailyTrend || []).map(row => ({
      ...row,
      date: row.day,
      value: row.totalReads ?? 0,
    })),
    messages: d?.messages || [],
    totalMessages: m.totalMessages ?? 0,
    startDate: d?.startDate,
    endDate: d?.endDate,
  };
}

export const analyticsApi = createApi({
  reducerPath: 'analytics',
  baseQuery,
  tagTypes: ['AnalyticsChart'],
  endpoints: analyticsEndpoints,
});

export const {
  useGetActivationsQuery,
  useGetActiveUsersQuery,
  useGetMessageCountsQuery,
  useGetSessionsDailyQuery,
  useGetUsersByPlatformQuery,
  useGetSessionsPerTeamQuery,
  useGetSessionsByPlatformQuery,
  useGetActiveUsersPerTeamQuery,
  useGetMessageCountsPerTeamQuery,
  useGetCompareTeamsQuery,
  useGetTeamHealthIndexQuery,
  useGetFilterOptionsQuery,
  useGetActiveUsersSimpleQuery,
  useGetMessageCountsSimpleQuery,
  useGetSessionsDailySimpleQuery,
  useGetUsersByPlatformSimpleQuery,
  useGetActivationsPerTeamPostQuery,
  useGetInitiativeDashboardQuery,
  useGetInitiativeSummaryQuery,
  useGetInitiativeConversionsQuery,
  useGetInitiativeStatsQuery,
  useLazyExportInitiativeStatsQuery,
  useGetInitiativeCompareQuery,
  useGetEngagementLeaderboardsQuery,
} = analyticsApi;

export const { reducer: analyticsReducer, middleware: analyticsMiddleware } = analyticsApi;
